import { t } from "@lingui/core/macro";
import type { SubdivisionOption } from "@/api/types/moneygram";

export const getCountriesWithSubdivisions = () => ["USA", "MEX", "CAN"];

/**
 * US states that are not supported for cash-out operations
 */
export const UNSUPPORTED_US_STATES = [
	"US-AK",
	"US-HI",
	"US-LA",
	"US-NY",
] as const;

export const getSubdivisions = (countryCode: string): SubdivisionOption[] => {
	switch (countryCode) {
		case "USA":
			return getUsaSubdivisions();
		case "MEX":
			return getMexSubdivisions();
		case "CAN":
			return getCanSubdivisions();
		default:
			return [];
	}
};

/**
 * Find subdivision value by region name (case-insensitive)
 * @param regionName - The English name of the region/state (e.g., "Colorado", "Alberta")
 * @param countryCode - The ISO3 country code (e.g., "USA", "CAN", "MEX")
 * @returns The subdivision value (e.g., "US-CO") or null if not found
 */
export const findSubdivisionByRegionName = (
	regionName: string,
	countryCode: string,
): string | null => {
	if (!regionName || !countryCode) {
		return null;
	}

	// Only support countries that have subdivisions
	if (!["USA", "MEX", "CAN"].includes(countryCode)) {
		return null;
	}

	const subdivisions = getSubdivisions(countryCode);
	const normalizedRegionName = regionName.toLowerCase().trim();

	// Find subdivision by case-insensitive name comparison
	const matchingSubdivision = subdivisions.find(
		(subdivision) => subdivision.name.toLowerCase() === normalizedRegionName,
	);

	return matchingSubdivision ? matchingSubdivision.value : null;
};

const getUsaSubdivisions = (): SubdivisionOption[] => [
	{ value: "US-AL", code: "AL", name: "Alabama", label: t`Alabama` },
	{ value: "US-AK", code: "AK", name: "Alaska", label: t`Alaska` },
	{ value: "US-AZ", code: "AZ", name: "Arizona", label: t`Arizona` },
	{ value: "US-AR", code: "AR", name: "Arkansas", label: t`Arkansas` },
	{ value: "US-CA", code: "CA", name: "California", label: t`California` },
	{ value: "US-CO", code: "CO", name: "Colorado", label: t`Colorado` },
	{ value: "US-CT", code: "CT", name: "Connecticut", label: t`Connecticut` },
	{
		value: "US-DC",
		code: "DC",
		name: "District of Columbia",
		label: t`District of Columbia`,
	},
	{ value: "US-DE", code: "DE", name: "Delaware", label: t`Delaware` },
	{ value: "US-FL", code: "FL", name: "Florida", label: t`Florida` },
	{ value: "US-GA", code: "GA", name: "Georgia", label: t`Georgia` },
	{ value: "US-HI", code: "HI", name: "Hawaii", label: t`Hawaii` },
	{ value: "US-ID", code: "ID", name: "Idaho", label: t`Idaho` },
	{ value: "US-IL", code: "IL", name: "Illinois", label: t`Illinois` },
	{ value: "US-IN", code: "IN", name: "Indiana", label: t`Indiana` },
	{ value: "US-IA", code: "IA", name: "Iowa", label: t`Iowa` },
	{ value: "US-KS", code: "KS", name: "Kansas", label: t`Kansas` },
	{ value: "US-KY", code: "KY", name: "Kentucky", label: t`Kentucky` },
	{ value: "US-LA", code: "LA", name: "Louisiana", label: t`Louisiana` },
	{ value: "US-ME", code: "ME", name: "Maine", label: t`Maine` },
	{ value: "US-MD", code: "MD", name: "Maryland", label: t`Maryland` },
	{
		value: "US-MA",
		code: "MA",
		name: "Massachusetts",
		label: t`Massachusetts`,
	},
	{ value: "US-MI", code: "MI", name: "Michigan", label: t`Michigan` },
	{ value: "US-MN", code: "MN", name: "Minnesota", label: t`Minnesota` },
	{ value: "US-MS", code: "MS", name: "Mississippi", label: t`Mississippi` },
	{ value: "US-MO", code: "MO", name: "Missouri", label: t`Missouri` },
	{ value: "US-MT", code: "MT", name: "Montana", label: t`Montana` },
	{ value: "US-NE", code: "NE", name: "Nebraska", label: t`Nebraska` },
	{ value: "US-NV", code: "NV", name: "Nevada", label: t`Nevada` },
	{
		value: "US-NH",
		code: "NH",
		name: "New Hampshire",
		label: t`New Hampshire`,
	},
	{ value: "US-NJ", code: "NJ", name: "New Jersey", label: t`New Jersey` },
	{ value: "US-NM", code: "NM", name: "New Mexico", label: t`New Mexico` },
	{ value: "US-NY", code: "NY", name: "New York", label: t`New York` },
	{
		value: "US-NC",
		code: "NC",
		name: "North Carolina",
		label: t`North Carolina`,
	},
	{ value: "US-ND", code: "ND", name: "North Dakota", label: t`North Dakota` },
	{ value: "US-OH", code: "OH", name: "Ohio", label: t`Ohio` },
	{ value: "US-OK", code: "OK", name: "Oklahoma", label: t`Oklahoma` },
	{ value: "US-OR", code: "OR", name: "Oregon", label: t`Oregon` },
	{ value: "US-PA", code: "PA", name: "Pennsylvania", label: t`Pennsylvania` },
	{ value: "US-RI", code: "RI", name: "Rhode Island", label: t`Rhode Island` },
	{
		value: "US-SC",
		code: "SC",
		name: "South Carolina",
		label: t`South Carolina`,
	},
	{ value: "US-SD", code: "SD", name: "South Dakota", label: t`South Dakota` },
	{ value: "US-TN", code: "TN", name: "Tennessee", label: t`Tennessee` },
	{ value: "US-TX", code: "TX", name: "Texas", label: t`Texas` },
	{ value: "US-UT", code: "UT", name: "Utah", label: t`Utah` },
	{ value: "US-VT", code: "VT", name: "Vermont", label: t`Vermont` },
	{ value: "US-VA", code: "VA", name: "Virginia", label: t`Virginia` },
	{ value: "US-WA", code: "WA", name: "Washington", label: t`Washington` },
	{
		value: "US-WV",
		code: "WV",
		name: "West Virginia",
		label: t`West Virginia`,
	},
	{ value: "US-WI", code: "WI", name: "Wisconsin", label: t`Wisconsin` },
	{ value: "US-WY", code: "WY", name: "Wyoming", label: t`Wyoming` },
];

const getMexSubdivisions = (): SubdivisionOption[] => [
	{
		value: "MX-AGU",
		code: "AGU",
		name: "Aguascalientes",
		label: t`Aguascalientes`,
	},
	{
		value: "MX-BCN",
		code: "BCN",
		name: "Baja California",
		label: t`Baja California`,
	},
	{
		value: "MX-BCS",
		code: "BCS",
		name: "Baja California Sur",
		label: t`Baja California Sur`,
	},
	{ value: "MX-CAM", code: "CAM", name: "Campeche", label: t`Campeche` },
	{ value: "MX-CHH", code: "CHH", name: "Chihuahua", label: t`Chihuahua` },
	{ value: "MX-CHP", code: "CHP", name: "Chiapas", label: t`Chiapas` },
	{ value: "MX-COA", code: "COA", name: "Coahuila", label: t`Coahuila` },
	{ value: "MX-COL", code: "COL", name: "Colima", label: t`Colima` },
	{
		value: "MX-DIF",
		code: "DIF",
		name: "Ciudad de México",
		label: t`Ciudad de México`,
	},
	{ value: "MX-DUR", code: "DUR", name: "Durango", label: t`Durango` },
	{ value: "MX-GRO", code: "GRO", name: "Guerrero", label: t`Guerrero` },
	{ value: "MX-GUA", code: "GUA", name: "Guanajuato", label: t`Guanajuato` },
	{ value: "MX-HID", code: "HID", name: "Hidalgo", label: t`Hidalgo` },
	{ value: "MX-JAL", code: "JAL", name: "Jalisco", label: t`Jalisco` },
	{ value: "MX-MEX", code: "MEX", name: "México", label: t`México` },
	{ value: "MX-MIC", code: "MIC", name: "Michoacán", label: t`Michoacán` },
	{ value: "MX-MOR", code: "MOR", name: "Morelos", label: t`Morelos` },
	{ value: "MX-NAY", code: "NAY", name: "Nayarit", label: t`Nayarit` },
	{ value: "MX-NLE", code: "NLE", name: "Nuevo León", label: t`Nuevo León` },
	{ value: "MX-OAX", code: "OAX", name: "Oaxaca", label: t`Oaxaca` },
	{ value: "MX-PUE", code: "PUE", name: "Puebla", label: t`Puebla` },
	{ value: "MX-QUE", code: "QUE", name: "Querétaro", label: t`Querétaro` },
	{
		value: "MX-ROO",
		code: "ROO",
		name: "Quintana Roo",
		label: t`Quintana Roo`,
	},
	{ value: "MX-SIN", code: "SIN", name: "Sinaloa", label: t`Sinaloa` },
	{
		value: "MX-SLP",
		code: "SLP",
		name: "San Luis Potosí",
		label: t`San Luis Potosí`,
	},
	{ value: "MX-SON", code: "SON", name: "Sonora", label: t`Sonora` },
	{ value: "MX-TAB", code: "TAB", name: "Tabasco", label: t`Tabasco` },
	{ value: "MX-TAM", code: "TAM", name: "Tamaulipas", label: t`Tamaulipas` },
	{ value: "MX-TLA", code: "TLA", name: "Tlaxcala", label: t`Tlaxcala` },
	{ value: "MX-VER", code: "VER", name: "Veracruz", label: t`Veracruz` },
	{ value: "MX-YUC", code: "YUC", name: "Yucatán", label: t`Yucatán` },
	{ value: "MX-ZAC", code: "ZAC", name: "Zacatecas", label: t`Zacatecas` },
];

const getCanSubdivisions = (): SubdivisionOption[] => [
	{ value: "CA-AB", code: "AB", name: "Alberta", label: t`Alberta` },
	{
		value: "CA-BC",
		code: "BC",
		name: "British Columbia",
		label: t`British Columbia`,
	},
	{ value: "CA-MB", code: "MB", name: "Manitoba", label: t`Manitoba` },
	{
		value: "CA-NB",
		code: "NB",
		name: "New Brunswick",
		label: t`New Brunswick`,
	},
	{
		value: "CA-NL",
		code: "NL",
		name: "Newfoundland and Labrador",
		label: t`Newfoundland and Labrador`,
	},
	{ value: "CA-NS", code: "NS", name: "Nova Scotia", label: t`Nova Scotia` },
	{
		value: "CA-NT",
		code: "NT",
		name: "Northwest Territories",
		label: t`Northwest Territories`,
	},
	{ value: "CA-NU", code: "NU", name: "Nunavut", label: t`Nunavut` },
	{ value: "CA-ON", code: "ON", name: "Ontario", label: t`Ontario` },
	{
		value: "CA-PE",
		code: "PE",
		name: "Prince Edward Island",
		label: t`Prince Edward Island`,
	},
	{ value: "CA-QC", code: "QC", name: "Quebec", label: t`Quebec` },
	{ value: "CA-SK", code: "SK", name: "Saskatchewan", label: t`Saskatchewan` },
	{
		value: "CA-YT",
		code: "YT",
		name: "Yukon Territory",
		label: t`Yukon Territory`,
	},
];
